import { useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import { errorMessage, formatDateTime, fullName } from '../lib/format';
import { useTable } from '../lib/useTable';
import { CLASS_INTEREST_OPTIONS, type Inquiry, type InquiryStatus } from '../lib/types';
import { PageHead } from '../components/AppLayout';
import { Modal } from '../components/Modal';
import { ClassPicker } from '../components/ClassPicker';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

const STATUS_LABEL: Record<InquiryStatus, { text: string; cls: string }> = {
  new: { text: 'New', cls: 'badge-warn' },
  booked: { text: 'Booked', cls: 'badge-success' },
  dismissed: { text: 'Dismissed', cls: 'badge-neutral' },
};

export function InquiriesPage() {
  const { rows, loading, error, reload } = useTable<Inquiry>('inquiries', 'created_at', {
    ascending: false,
  });
  const toast = useToast();
  const confirm = useConfirm();
  const [viewing, setViewing] = useState<Inquiry | null>(null);
  const [noting, setNoting] = useState<Inquiry | null>(null);
  const [addingToClass, setAddingToClass] = useState<Inquiry | null>(null);

  async function remove(row: Inquiry) {
    const ok = await confirm({
      title: `Remove ${fullName(row)}'s inquiry?`,
      body: "This can't be undone.",
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    const { error } = await supabase.from('inquiries').delete().eq('id', row.id);
    if (error) toast.error(errorMessage(error));
    else {
      toast.success('Inquiry removed.');
      void reload();
    }
  }

  return (
    <>
      <PageHead
        title="Inquiries"
        subtitle="Submissions from the website's Inquire form. Add one to a class to turn them into an enrolled client."
      />

      <div className="card">
        {error && <p className="error-text">{error}</p>}
        {loading ? (
          <p className="muted">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="empty">
            <span className="empty-emoji">📨</span>
            No inquiries yet. New submissions from the website will show up here.
          </div>
        ) : (
          <table className="table cards">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Name</th>
                <th>Dog breed</th>
                <th>Dog name</th>
                <th>Phone</th>
                <th>Interested in</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="muted small" data-label="Submitted">
                    {formatDateTime(row.created_at)}
                  </td>
                  <td style={{ fontWeight: 600 }}>{fullName(row)}</td>
                  <td className="muted" data-label="Dog breed">
                    {row.dog_breed || '—'}
                  </td>
                  <td className="muted" data-label="Dog name">
                    {row.dog_name || '—'}
                  </td>
                  <td className="muted" data-label="Phone">
                    {row.phone || '—'}
                  </td>
                  <td className="muted" data-label="Interested in">
                    {row.class_interest || '—'}
                  </td>
                  <td data-label="Status">
                    <span className={`badge ${STATUS_LABEL[row.status].cls}`}>
                      {STATUS_LABEL[row.status].text}
                    </span>
                  </td>
                  <td>
                    <div className="row" style={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setAddingToClass(row)}
                      >
                        Add to class
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setViewing(row)}>
                        View
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setNoting(row)}>
                        Note
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => void remove(row)}>
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {viewing && (
        <ViewModal
          inquiry={viewing}
          onClose={() => setViewing(null)}
          onSaved={() => {
            setViewing(null);
            void reload();
          }}
        />
      )}

      {noting && (
        <NoteForm
          inquiry={noting}
          onClose={() => setNoting(null)}
          onSaved={() => {
            setNoting(null);
            void reload();
          }}
        />
      )}

      {addingToClass && (
        <AddToClassModal
          inquiry={addingToClass}
          onClose={() => setAddingToClass(null)}
          onAdded={() => {
            setAddingToClass(null);
            void reload();
          }}
        />
      )}
    </>
  );
}

// ── Add to class (books the inquiry: creates the client, enrolls them,
// then clears the inquiry) ──────────────────────────────────────────
function AddToClassModal({
  inquiry,
  onClose,
  onAdded,
}: {
  inquiry: Inquiry;
  onClose: () => void;
  onAdded: () => void;
}) {
  const toast = useToast();
  const [classId, setClassId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!classId) return;
    setBusy(true);
    setErr(null);
    const { data: created, error: clientError } = await supabase
      .from('clients')
      .insert({
        first_name: inquiry.first_name,
        last_name: inquiry.last_name,
        phone: inquiry.phone,
        email: inquiry.email,
        dog_name: inquiry.dog_name,
        breed: inquiry.dog_breed,
        // clients.age is a whole number of years; the inquiry's age is
        // free text ("12 weeks") so only carry it over when it's clearly
        // a number, otherwise leave it for the owner to fill in.
        age:
          inquiry.dog_age && /^\d+$/.test(inquiry.dog_age.trim()) ? Number(inquiry.dog_age) : null,
        notes: inquiry.comments,
      })
      .select('id')
      .single();
    if (clientError || !created) {
      setBusy(false);
      setErr(errorMessage(clientError));
      return;
    }
    const { error: enrollError } = await supabase
      .from('enrollments')
      .insert({ class_id: classId, client_id: created.id });
    if (enrollError) {
      setBusy(false);
      setErr(errorMessage(enrollError));
      return;
    }
    const { error: deleteError } = await supabase.from('inquiries').delete().eq('id', inquiry.id);
    setBusy(false);
    if (deleteError) toast.error(errorMessage(deleteError));
    toast.success(`${fullName(inquiry)} added as a client and enrolled.`);
    onAdded();
  }

  return (
    <Modal title={`Add ${fullName(inquiry)} to a class`} onClose={onClose}>
      <form onSubmit={submit}>
        <ClassPicker value={classId} onChange={setClassId} />
        {err && <p className="error-text">{err}</p>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !classId}>
            {busy ? 'Adding…' : 'Add to class'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── View / Edit modal ────────────────────────────────────────────────
// Defaults to a clean read-only summary; "Edit" swaps the same fields
// in for editing, in place, instead of a separate Edit button/dialog.
function ViewModal({
  inquiry,
  onClose,
  onSaved,
}: {
  inquiry: Inquiry;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <InquiryForm
        inquiry={inquiry}
        onClose={() => setEditing(false)}
        onCancelAll={onClose}
        onSaved={onSaved}
      />
    );
  }

  const detail = (label: string, value: string | null) =>
    value ? (
      <div className="iq-detail-row">
        <span className="iq-detail-label">{label}</span>
        <span>{value}</span>
      </div>
    ) : null;

  return (
    <Modal
      title={fullName(inquiry)}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="btn btn-primary" onClick={() => setEditing(true)}>
            Edit
          </button>
        </>
      }
    >
      <div className="iq-detail">
        <div className="iq-detail-row">
          <span className="iq-detail-label">Status</span>
          <span className={`badge ${STATUS_LABEL[inquiry.status].cls}`}>
            {STATUS_LABEL[inquiry.status].text}
          </span>
        </div>
        {detail('Submitted', formatDateTime(inquiry.created_at))}
        {detail('Phone', inquiry.phone)}
        {detail('Email', inquiry.email)}
        {detail('Dog name', inquiry.dog_name)}
        {detail('Dog breed', inquiry.dog_breed)}
        {detail('Dog age', inquiry.dog_age)}
        {detail('Interested in', inquiry.class_interest)}
        {inquiry.comments && (
          <div className="iq-detail-row iq-detail-block">
            <span className="iq-detail-label">Comments</span>
            <p style={{ margin: '0.3rem 0 0' }}>{inquiry.comments}</p>
          </div>
        )}
        {inquiry.notes && (
          <div className="iq-detail-row iq-detail-block">
            <span className="iq-detail-label">Staff note</span>
            <p style={{ margin: '0.3rem 0 0' }}>📝 {inquiry.notes}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

function InquiryForm({
  inquiry,
  onClose,
  onCancelAll,
  onSaved,
}: {
  inquiry: Inquiry;
  onClose: () => void;
  onCancelAll: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [f, setF] = useState({
    first_name: inquiry.first_name,
    last_name: inquiry.last_name,
    phone: inquiry.phone ?? '',
    email: inquiry.email ?? '',
    dog_name: inquiry.dog_name ?? '',
    dog_breed: inquiry.dog_breed ?? '',
    dog_age: inquiry.dog_age ?? '',
    class_interest: inquiry.class_interest ?? '',
    comments: inquiry.comments ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((prev) => ({ ...prev, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const { error } = await supabase
      .from('inquiries')
      .update({
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        phone: f.phone.trim() || null,
        email: f.email.trim() || null,
        dog_name: f.dog_name.trim() || null,
        dog_breed: f.dog_breed.trim() || null,
        dog_age: f.dog_age.trim() || null,
        class_interest: f.class_interest || null,
        comments: f.comments.trim() || null,
      })
      .eq('id', inquiry.id);
    setBusy(false);
    if (error) setErr(errorMessage(error));
    else {
      toast.success('Inquiry updated.');
      onSaved();
    }
  }

  return (
    <Modal title={`Edit — ${fullName(inquiry)}`} onClose={onCancelAll}>
      <form onSubmit={submit}>
        <div className="field-row">
          <div className="field">
            <label htmlFor="iq-first">First name</label>
            <input
              id="iq-first"
              value={f.first_name}
              onChange={(e) => set('first_name', e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="field">
            <label htmlFor="iq-last">Last name</label>
            <input
              id="iq-last"
              value={f.last_name}
              onChange={(e) => set('last_name', e.target.value)}
              required
            />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="iq-phone">Phone</label>
            <input id="iq-phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="iq-email">Email</label>
            <input
              id="iq-email"
              type="email"
              value={f.email}
              onChange={(e) => set('email', e.target.value)}
            />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="iq-dog">Dog name</label>
            <input
              id="iq-dog"
              value={f.dog_name}
              onChange={(e) => set('dog_name', e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="iq-breed">Breed</label>
            <input
              id="iq-breed"
              value={f.dog_breed}
              onChange={(e) => set('dog_breed', e.target.value)}
            />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="iq-age">Dog age</label>
            <input
              id="iq-age"
              placeholder="e.g. 2 years or 12 weeks"
              value={f.dog_age}
              onChange={(e) => set('dog_age', e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="iq-interest">Type of class</label>
            <select
              id="iq-interest"
              value={f.class_interest}
              onChange={(e) => set('class_interest', e.target.value)}
            >
              <option value="">—</option>
              {CLASS_INTEREST_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="iq-comments">Comments</label>
          <textarea
            id="iq-comments"
            value={f.comments}
            onChange={(e) => set('comments', e.target.value)}
          />
        </div>
        {err && <p className="error-text">{err}</p>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Back
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || !f.first_name.trim() || !f.last_name.trim()}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function NoteForm({
  inquiry,
  onClose,
  onSaved,
}: {
  inquiry: Inquiry;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [notes, setNotes] = useState(inquiry.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const { error } = await supabase
      .from('inquiries')
      .update({ notes: notes.trim() || null })
      .eq('id', inquiry.id);
    setBusy(false);
    if (error) setErr(errorMessage(error));
    else {
      toast.success('Note saved.');
      onSaved();
    }
  }

  return (
    <Modal title={`Note — ${fullName(inquiry)}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="iq-note">Staff note</label>
          <textarea
            id="iq-note"
            rows={5}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything to remember about this inquiry…"
            autoFocus
          />
        </div>
        {err && <p className="error-text">{err}</p>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save note'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
